import { type AuthSession, type LoginRequest } from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';
import { loggedIn } from '@/shared/store/reducers/auth/auth.slice';

/**
 * Signing in — the Auth module's own surface.
 *
 * `/me` and `/auth/logout` are deliberately NOT here: the shell needs both on
 * every page, and `shared/` may not import a module (W2), so they live in
 * `@/shared/services/session/session.api`. Login is used from exactly one route and
 * nothing shared depends on it.
 *
 * The mutation writes the tokens into the auth slice from `onQueryStarted`
 * rather than leaving that to the caller: a component that forgets the dispatch
 * produces a successful login with no session, which presents as a wrong
 * password. Publishing `auth.signedIn` (W5) lets anything that cares react
 * without this module knowing who they are.
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    login: builder.mutation<AuthSession, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(loggedIn(data.tokens));
          EVENT_BUS.publish({ type: 'auth.signedIn', userId: data.user.id });
        } catch {
          // A refused sign-in is not a session. Swallowed deliberately:
          // `onQueryStarted` has no caller, so a rejection escaping it becomes
          // an unhandled rejection; the form already reads the failure off the
          // mutation's own `error`.
        }
      },
    }),
  }),
});

export const { useLoginMutation } = authApi;
